/**
 * iLing Frontend Logger
 * =====================
 * Thin client-side event logger that batches events and ships them to the
 * backend /api/logs/frontend endpoint every 5 seconds.
 *
 * Design rules:
 * - Never throws — all errors are silently caught.
 * - Errors and API failures flush immediately (don't wait 5 s).
 * - Uses fetch keepalive:true so logs are sent even on page unload.
 * - Collects route, user, and action context automatically.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 100;

class FrontendLogger {
  constructor() {
    this._queue = [];
    this._timer = null;
    this._page = window.location.pathname;
    this._userId = null;
    this._initialized = false;
  }

  /** Call once after auth resolves. Starts the flush timer. */
  init(userId) {
    this._userId = userId || null;
    if (!this._initialized) {
      this._initialized = true;
      this._timer = setInterval(() => this._flush(), FLUSH_INTERVAL_MS);
      // Final flush on tab close
      window.addEventListener('beforeunload', () => this._flush(true));
    }
  }

  /** Update current page (call from route change effect). */
  setPage(path) {
    this._page = path || window.location.pathname;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Log a user action (button click, form submit, etc.) */
  action(name, details = {}) {
    this._enqueue('action', { name, ...details });
  }

  /** Log a frontend JS error. */
  error(message, details = {}) {
    this._enqueue('error', { message, ...details });
    this._flush();           // immediate on errors
  }

  /** Log an API call failure. */
  apiError(endpoint, status, message) {
    this._enqueue('api_error', { endpoint, status: status || 0, message: message || '' });
    this._flush();           // immediate on errors
  }

  /** Log a route/page navigation. */
  navigation(from, to) {
    this._enqueue('navigation', { from, to });
  }

  /** Log a timed performance mark. */
  perf(label, durationMs) {
    this._enqueue('perf', { label, duration_ms: Math.round(durationMs) });
  }

  // ── Internal ───────────────────────────────────────────────────────────────

  _enqueue(eventType, details) {
    if (this._queue.length >= MAX_QUEUE) return;   // drop if queue is full
    this._queue.push({
      event_type: eventType,
      page:       this._page,
      details:    { user_id: this._userId, ...details },
      timestamp:  new Date().toISOString(),
    });
  }

  async _flush(sync = false) {
    if (!this._queue.length) return;
    const batch = this._queue.splice(0, this._queue.length);
    try {
      await fetch(`${BACKEND_URL}/api/logs/frontend`, {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        credentials: 'include',
        body:        JSON.stringify({ events: batch }),
        keepalive:   true,
      });
    } catch (_) {
      // Silent — never crash the app for a log failure
    }
  }
}

export const logger = new FrontendLogger();
export default logger;
